import {
  KPI_CARD_STATIC_CONFIGS,
  MACHINE_UTILISATION_STATIC_CONFIG,
} from '../components/DoughnutKpiCard/DoughnutKpiCard.constants';
import { KpiCardConfig, KpiCardStaticConfig } from '../types/MachineDashboard.types';
import {
  BatteryDischargeStatus,
  DataStatus,
  GeofenceStatusType,
  MachineUtilizationStatus,
  ManualMachineDashboard,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';

const getCount = (counts: { status: string; count: number }[] | undefined, status: string): number =>
  counts?.find(item => item.status === status)?.count ?? 0;

const getSegmentValues = (cardId: string, data: ManualMachineDashboard): number[] => {
  switch (cardId) {
    case 'dataStatus':
      return [
        getCount(data.dataStatus, DataStatus.LessThanOneDayOld),
        getCount(data.dataStatus, DataStatus.OneToThreeDaysOld),
        getCount(data.dataStatus, DataStatus.GreaterThanThreeDaysOld),
        getCount(data.dataStatus, DataStatus.NoData),
      ];
    case 'geofencing':
      return [
        getCount(data.geofenceStatus, GeofenceStatusType.Inside),
        getCount(data.geofenceStatus, GeofenceStatusType.NoData),
        getCount(data.geofenceStatus, GeofenceStatusType.Outside),
      ];
    case 'batteryStatus':
      return [
        getCount(data.batteryDischargeStatus, BatteryDischargeStatus.Ok),
        getCount(data.batteryDischargeStatus, BatteryDischargeStatus.Critical),
      ];
    case 'machineUtilisation':
      return [
        getCount(data.machineUtilizationStatus, MachineUtilizationStatus.Used),
        getCount(data.machineUtilizationStatus, MachineUtilizationStatus.NotUsed),
      ];
    default:
      return [];
  }
};

const buildKpiCardConfig = (config: KpiCardStaticConfig, data?: ManualMachineDashboard | null): KpiCardConfig => {
  const values = data ? getSegmentValues(config.id, data) : config.segmentLabels.map(() => 0);
  const total = values.reduce((sum, value) => sum + value, 0);

  let centerPercent = 0;

  if (config.id === 'dataStatus') {
    centerPercent = total > 0 ? Math.round(((values[0] + values[1]) / total) * 100) : 0;
  } else {
    centerPercent = total > 0 ? Math.round((values[0] / total) * 100) : 0;
  }

  return {
    id: config.id,
    title: config.title,
    tooltip: config.tooltip,
    centerLabel: config.centerLabel,
    listTitle: config.listTitle,
    alertSegmentIndex: config.alertSegmentIndex,
    alertText: config.alertText,
    hintText: config.hintText,
    emptyDescription: config.emptyDescription,
    segments: config.segmentLabels.map((label, index) => ({
      ...label,
      value: values[index] ?? 0,
    })),
    centerPercent,
  };
};

export const buildKpiCardConfigs = (data?: ManualMachineDashboard | null): KpiCardConfig[] =>
  KPI_CARD_STATIC_CONFIGS.map(config => buildKpiCardConfig(config, data));

export const buildMachineUtilisationKpiConfig = (data?: ManualMachineDashboard | null): KpiCardConfig =>
  buildKpiCardConfig(MACHINE_UTILISATION_STATIC_CONFIG, data);
